import * as angular from 'angular';
import * as _ from 'lodash';
import * as d3 from 'd3';

import { WidgetWithErrors, IdataWidget } from '../../widgets';
import { Helper } from '../../common';
import { ReadType, IBindable, WidgetValuesReadConfiguration } from '../../models';
import { IwidgetIntervalHelperService } from '../../services';
import { IExportable } from '../../widgets/models/interfaces';
import { ScatterConfiguration } from './scatter.configuration';
import { ScatterWidgetDefinition as definition } from '../../widgets/widgets.definition.index';

export class ScatterWidget extends WidgetWithErrors implements IExportable, IdataWidget {
    baseType = definition.baseType;
    col = 0;
    configuration: ScatterConfiguration;
    exportToCsv: () => angular.IPromise<any>;
    guid: string;
    hasExtendedProperties = true;
    isDisabledMaximized = false;
    isExportable = true;
    isMaximizable = true;
    isMaximized = false;
    isTitleHidden = false;
    isZoomable = true;
    name = definition.title;
    row = 0;
    sizeX = 6;
    sizeY = 18;
    type = definition.type;

    constructor(
        private _$q: angular.IQService,
        intervalHelper: IwidgetIntervalHelperService
    ) {
        super(intervalHelper);
        this.guid = Helper.getGuid();
        this.configuration = new ScatterConfiguration();
    }

    getSeriesGuids(): string[] {
        return this.configuration.series.map(d => d.guid);
    }

    getReadConfiguration(beginDate, endDate): WidgetValuesReadConfiguration {
        const widgetValuesReadConfiguration = this.getReadConfigurationBase(beginDate, endDate,
            this.configuration.series, this.configuration,
            this.configuration.aggregation,
            false, false, false, false, true);
        if (this.configuration.shapes && this.configuration.shapes.length) {
            const startIndex = widgetValuesReadConfiguration.series?.length ? d3.max(widgetValuesReadConfiguration.series, s => s.id) : -1;
            widgetValuesReadConfiguration.extraValues = this.getExtraValues(this.configuration.shapes, startIndex);
        }
        return widgetValuesReadConfiguration;
    }

    private getExtraValues(shapes: any[], startIndex: number): {
        readTypes: ReadType[];
        aggregationMode: any;
        series: { guid: string; value: string; isBoundToGroup: boolean; id: number }[];
    }[] {
        let idx = startIndex;
        return shapes.map(s => {
            const points = s.points ? s.points : [];
            return {
                readTypes: [ReadType.AggregatedValue],
                aggregationMode: s.aggregationMode,
                series: points.map(p => p.x)
                    .concat(points.map(p => p.y))
                    .filter(p => p.type === 'serie')
                    .map((p, i) => {
                        idx++;
                        return {
                            guid: p.seriesGuid,
                            value: p.valueType,
                            isBoundToGroup: p.isBoundToGroup,
                            id: idx
                        };
                    })
            };
        });
    }

    //TODO exactly the same as tagstrendwidget
    private getSeriesListAsync(): angular.IPromise<IBindable[]> {
        const result = this.configuration.series.map(d => {
            return {
                guid: d.guid,
                originalGuid: d.guid,
                value: d.value,
                relativePath: d.relativePath,
                isBoundToGroup: d.isBoundToGroup
            };
        });
        return this._$q.when(result);
    }

    getReadConfigurationAsync(): angular.IPromise<WidgetValuesReadConfiguration> {
        return this.getSeriesListAsync()
            .then(data => {
                const hasQuality = false;
                const hasMissingRanges = false;
                const hasContext = false;
                const hasTendency = false;
                const hasTarget = false;
                const aggregation = this.configuration.aggregation;
                const widgetValuesReadConfiguration = this.createConfiguration(data, aggregation, hasQuality,
                    hasMissingRanges, hasContext,
                    hasTendency, hasTarget);
                if (this.configuration.shapes && this.configuration.shapes.length) {
                    widgetValuesReadConfiguration.extraValues = this.getExtraValues(this.configuration.shapes, this.configuration.series.length);
                }
                return widgetValuesReadConfiguration;
            });
    }

    extraSeries(): IBindable[] {
        let result = [];
        if (this.configuration.shapes && this.configuration.shapes.length) {
            result = _.flatten(this.getExtraValues(this.configuration.shapes, this.configuration.series.length).map(d => d.series));
        }
        return result;
    }
}